import { ChronoDtoSchema, convertChronoFromDto, convertChronoToDto } from "@modules/chrono/services/chronoDtos"
import { UserDtoSchema, convertUserFromDto } from "@modules/profile/profileDtos"
import { DashboardScoreDtoSchema, ScoreBoardDetailDtoSchema } from "@modules/scoreboard/scoreDto"
import { ScoreType } from "@modules/scoreboard/scoreTypes"
import {
  Exercise,
  ExerciseCategories,
  ExerciseDashboard,
  ExerciseDraft,
  ExerciseScore,
  ExerciseScoreDifficulties,
  ProgramExerciseTypeOf,
  UserExerciseTypeOf,
} from "@modules/sessionBlock/exercises/exerciseTypes"
import { MediaItemDraftDtoSchema, MediaItemDtoSchema } from "@modules/sessionBlock/media/mediaDto"
import { convertToEnum, convertToType } from "@modules/utils/enum"
import { z } from "zod"

export const ExerciseScoreDtoSchema = z.object({
  value: z.number(),
  difficulty: z.string(),
  note: z.string().optional(),
  noteAndMediaIsVisible: z.boolean(),
  medias: z.array(MediaItemDtoSchema),
})

export type ExerciseScoreDto = z.infer<typeof ExerciseScoreDtoSchema>

const ExerciseScoreInputDtoSchema = z.object({
  score: z.number(),
  difficulty: z.string(),
  note: z.string().optional(),
  noteAndMediaIsVisible: z.boolean(),
  medias: z.array(MediaItemDraftDtoSchema),
})

export type ExerciseScoreInputDto = z.infer<typeof ExerciseScoreInputDtoSchema>

const ItemCommonExerciseDtoSchema = z.object({
  id: z.string(),
  title: z.string(),
  category: z.string(),
  otherCategory: z.string().optional(),
  chrono: ChronoDtoSchema.optional(),
  isFavorite: z.boolean(),
  type: z.union([z.literal(ProgramExerciseTypeOf), z.literal(UserExerciseTypeOf)]),
  owner: UserDtoSchema.optional(),
  rpe: z.number().nullable(),
  requiredMedia: z.boolean(),
  durationInMin: z.number().nullish(),
})

export type ItemCommonExerciseDto = z.infer<typeof ItemCommonExerciseDtoSchema>

const CommonExerciseDtoSchema = ItemCommonExerciseDtoSchema.extend({
  description: z.string(),
})

export type CommonExerciseDto = z.infer<typeof CommonExerciseDtoSchema>

const ItemExerciseDtoSchema = ItemCommonExerciseDtoSchema.extend({
  type: z.literal(ProgramExerciseTypeOf),
})

export type ItemExerciseDto = z.infer<typeof ItemExerciseDtoSchema>

export const ExerciseDtoSchema = CommonExerciseDtoSchema.extend({
  type: z.literal(ProgramExerciseTypeOf),
  isCompleted: z.boolean(),
  scoreType: z.string().optional(),
  score: ScoreBoardDetailDtoSchema.optional(),
})

export type ExerciseDto = z.infer<typeof ExerciseDtoSchema>

const CommonExerciseDraftDtoSchema = z.object({
  id: z.string().nullable(),
  title: z.string(),
  description: z.string(),
  category: z.string(),
  chrono: ChronoDtoSchema.optional(),
})

export type CommonExerciseDraftDto = z.infer<typeof CommonExerciseDraftDtoSchema>

export const ExerciseDraftDtoSchema = CommonExerciseDraftDtoSchema.extend({
  scoreType: z.string(),
})

export type ExerciseDraftDto = z.infer<typeof ExerciseDraftDtoSchema>

const ExerciseDashboardDtoSchema = z.object({
  totalScore: z.number(),
  topUsers: z.array(
    z.object({
      score: DashboardScoreDtoSchema,
      user: UserDtoSchema,
    }),
  ),
  lastRecordUsers: z.array(
    z.object({
      score: DashboardScoreDtoSchema,
      user: UserDtoSchema,
    }),
  ),
})

export type ExerciseDashboardDto = z.infer<typeof ExerciseDashboardDtoSchema>

function convertItemCommonExerciseFromDto(dto: ItemExerciseDto) {
  return {
    id: dto.id,
    _type: dto.type,
    title: dto.title,
    category: convertToType(ExerciseCategories, dto.category, "OTHER"),
    otherCategory: dto.otherCategory,
    chrono: dto.chrono ? convertChronoFromDto(dto.chrono) : undefined,
    isFavorite: dto.isFavorite,
    creator: dto.owner ? convertUserFromDto(dto.owner) : undefined,
    rpe: dto.rpe,
    requiredMedia: dto.requiredMedia,
    durationInMin: dto.durationInMin ?? undefined,
  }
}

function convertCommonExerciseFromDto(dto: ExerciseDto) {
  return {
    ...convertItemCommonExerciseFromDto(dto),
    description: dto.description,
  }
}

export function convertExerciseFromDto(dto: ExerciseDto): Exercise {
  return {
    ...convertCommonExerciseFromDto(dto),
    _type: ProgramExerciseTypeOf,
    isCompleted: dto.isCompleted ?? false,
    scoreType: convertToEnum(ScoreType, dto.scoreType, ScoreType.NO_SCORE),
    score: dto.score,
  }
}

export function convertCommonExerciseToDto(draft: ExerciseDraft) {
  return {
    id: draft.id,
    title: draft.title,
    description: draft.description,
    category: draft.category,
    otherCategory: draft.otherCategory,
    chrono: draft.chrono ? convertChronoToDto(draft.chrono) : undefined,
    rpe: draft.rpe,
    requiredMedia: draft.requiredMedia,
    durationInMin: draft.durationInMin,
  }
}

export function convertExerciseToDto(draft: ExerciseDraft): ExerciseDraftDto {
  return { ...convertCommonExerciseToDto(draft), scoreType: draft.scoreType ?? ScoreType.NO_SCORE }
}

export function convertExerciseScoreFromDto(dto: ExerciseScoreDto | null): ExerciseScore | undefined {
  if (!dto) return undefined

  return {
    value: dto.value,
    difficulty: convertToType(ExerciseScoreDifficulties, dto.difficulty, "RX"),
    note: dto.note || undefined,
    medias: dto.medias,
  }
}

export function convertExerciseDashboardFromDto(dto: ExerciseDashboardDto): ExerciseDashboard {
  return {
    ...dto,
    topUsers: dto.topUsers.map((topUser) => ({
      user: convertUserFromDto(topUser.user),
      score: {
        value: topUser.score.value,
        type: topUser.score.type as ScoreType,
      },
    })),
    lastRecordUsers: dto.lastRecordUsers.map((lastRecordUser) => ({
      ...lastRecordUser,
      user: convertUserFromDto(lastRecordUser.user),
      score: {
        value: lastRecordUser.score.value,
        type: lastRecordUser.score.type as ScoreType,
      },
    })),
  }
}
